import React, { useState, useEffect } from 'react';
import { Chip, Tooltip, Typography, makeStyles } from '@material-ui/core';
import { Button, BoxWrapper } from 'components/Base';
import { compose } from 'ramda';
import { formatPercent } from 'utils';
import Store from 'store/store';
import FixedBenchmark from '../FixedBenchmark/FixedBenchmark';
import BenchmarkTable from 'components/Benchmarks/BenchmarkTable';
import CancelIcon from '@material-ui/icons/Cancel';

const { connect, selectors } = Store;

const useStyles = makeStyles({
  wrapper: {
    width: '90vw'
  },
  empty: {
    textAlign: 'center',
    padding: '2rem',
    width: '100%'
  },
  box: {
    maxHeight: '85vh',
    overflow: 'auto'
  },
  title: {
    fontSize: '1.1rem',
    lineHeight: '1.3rem',
    fontWeight: 600,
    marginBottom: '0.5rem'
  },
  btnWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '1rem',
    borderTop: '1px solid #F0F0F0'
  },
  chip: {
    padding: '1.2rem 0.5rem',
    fontWeight: 500
  }
});

interface BenchmarkManagerProps {
  closeModal: Function;
  fixedBenchmarks: any[];
  benchmarkList: any[];
  saveBenchmarks: Function;
  identifier?: any;
}

const BenchmarkManager = ({
  closeModal,
  fixedBenchmarks,
  benchmarkList,
  saveBenchmarks,
  identifier = null
}: BenchmarkManagerProps) => {
  const classes = useStyles();
  const [customBenchmarks, setCustomBenchmarks] = useState(
    fixedBenchmarks.filter((fixedBenchmark: any) => {
      return !fixedBenchmark.benchmark_id;
    })
  );
  const [fullBenchmarkList, setFullBenchmarkList] = useState(benchmarkList);
  const [definedBenchmarks, setDefinedBenchmarks] = useState(benchmarkList);
  const [selectedBenchmarks, setSelectedBenchmarks] = useState([] as any);
  const [page, setPage] = useState('manager');
  const cap = 12;

  const updateSelectedBenchmarks = () => {
    setSelectedBenchmarks([
      ...definedBenchmarks.filter((benchmark: any) => benchmark.selected),
      ...customBenchmarks.map((customBenchmark: any) => ({
        ...customBenchmark,
        benchmarkType: 'fixed',
        name: `Fixed Benchmark - ${formatPercent(
          Number(customBenchmark.annual_increase) * 100
        )} PA`
      }))
    ]);
  };

  const addFixedBenchmark = (benchmark: any) => {
    setCustomBenchmarks([...customBenchmarks, benchmark]);
  };

  const deleteBenchmark = (benchmark: any, index: number) => {
    if (benchmark.benchmarkType === 'fixed') {
      const newBenchmarks = [...customBenchmarks];
      newBenchmarks.splice(
        index -
          definedBenchmarks.filter((benchmark: any) => benchmark.selected)
            .length,
        1
      );
      setCustomBenchmarks(newBenchmarks);
    } else {
      index = definedBenchmarks.findIndex(
        (definedBenchmark: any) => definedBenchmark.value === benchmark.value
      );
      const newBenchmarks = [...definedBenchmarks];
      newBenchmarks.splice(index, 1, {
        ...newBenchmarks[index],
        selected: !newBenchmarks[index].selected
      });
      setFullBenchmarkList(newBenchmarks);
      setDefinedBenchmarks(newBenchmarks);
    }
  };

  useEffect(() => {
    updateSelectedBenchmarks();
  }, [definedBenchmarks, customBenchmarks]);

  return (
    <BoxWrapper
      className={page === 'manager' ? classes.wrapper : ''}
      title={page === 'new' ? 'Add Fixed Benchmark' : 'Manage Benchmarks'}
      subTitle={
        page === 'new'
          ? ''
          : `Select up to ${cap} benchmarks from a variety of categories, including IA Sector, ABI Sector, Morningstar Categories,  ARC, Cash, Inflation and Property. Alternatively, you can add custom fixed benchmarks to suit your specific needs. Benchmarks help you evaluate performance and make informed comparisons for your investment strategies.`
      }
      closeModal={closeModal}
    >
      <div className={classes.box}>
        {page === 'new' && (
          <FixedBenchmark
            closeModal={() => setPage('manager')}
            addFixedBenchmark={addFixedBenchmark}
          />
        )}
        <div style={{ display: page === 'new' ? 'none' : 'initial' }}>
          <div style={{ padding: '1rem' }}>
            <Typography variant="h3" className={classes.title}>
              Selected
            </Typography>
            {selectedBenchmarks.length > 0 ? (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {selectedBenchmarks.map((benchmark: any, index: number) => {
                  return (
                    <Chip
                      key={benchmark.value}
                      className={classes.chip}
                      color="primary"
                      deleteIcon={<CancelIcon />}
                      onDelete={() => {
                        deleteBenchmark(benchmark, index);
                      }}
                      label={benchmark.name}
                    />
                  );
                })}
              </div>
            ) : (
              'No benchmarks have been selected. Choose from the list below or add a fixed benchmark.'
            )}
            <div style={{ display: 'flex', marginTop: '0.5rem' }}>
              <Tooltip
                title={
                  cap === selectedBenchmarks.length
                    ? `You can only add up to ${cap} benchmarks.`
                    : ''
                }
                arrow
              >
                <Button
                  onClick={() => setPage('new')}
                  style={{ marginLeft: 'auto' }}
                  disabled={cap === selectedBenchmarks.length}
                >
                  Add Fixed Benchmark
                </Button>
              </Tooltip>
            </div>
            <div style={{ marginTop: '1rem' }}>
              <BenchmarkTable
                name={`${identifier}-benchmarks`}
                benchmarks={fullBenchmarkList}
                setBenchmarks={setDefinedBenchmarks}
                defaultPageSize={5}
                cap={cap}
                amountSelected={selectedBenchmarks.length + 1}
              />
            </div>
          </div>
          <div className={classes.btnWrapper}>
            <Button color="primary" onClick={() => closeModal()}>
              Cancel
            </Button>
            <Button onClick={() => saveBenchmarks(selectedBenchmarks)}>
              Confirm
            </Button>
          </div>
        </div>
      </div>
    </BoxWrapper>
  );
};

export default compose(
  connect((state: any) => ({
    benchmarks: selectors.metaSelectors.benchmarks(state)
  }))
)(BenchmarkManager);
